import React, { useEffect, useState } from 'react';
import { useSelector, connect } from 'react-redux';
import get from 'lodash/get';
import AdWithPlaceholder from '../../molecules/ad-with-placeholder';
import {
  isWithinTradingHours,
  makeApiCall,
  processApiValues,
  widgetLink,
} from '../../../helpers/utils';
import { BQLink } from '../../atoms/bqlink';
import { isStoryExclusive } from '../../../helpers/story-helpers';
import { FourStarIcon } from '../../atoms/material-icons';
import { contentClick } from '../../../helpers/datalayer-utils';
import { useInterval } from '../../../helpers/custom-hooks';
import './budget-stocks-in-focus-live.m.css';

const BudgetStocksInFocusLive = ({
  widget = false,
  darkMode = false,
  lightMode = false,
  showValues = false,
  pageType,
  stocks_radar,
}) => {
  const [widgetParam, setWidgetParam] = useState('');
  const [isDesktop, setIsDesktop] = useState(false);
  const [stockStories, setStockStories] = useState([]);
  const [displayName, setDisplayName] = useState('');
  const userData = useSelector((state) => get(state, ['userData']));
  const isSubscriber = useSelector((state) => get(state, ['userData', 'isSubscriber'], null));
  const tradingHolidays = useSelector((state) =>
    get(state, ['qt', 'config', 'features', 'trading_holidays'], []),
  );
  const specialTradingDays = useSelector((state) =>
    get(state, ['qt', 'config', 'features', 'special_trading_days'], {}),
  );

  useEffect(() => {
    const width = window.innerWidth;
    setIsDesktop(width > 767);
  }, []);

  useEffect(() => {
    fetchData();
    const urlParams = new URLSearchParams(window.location.search);
    const view = urlParams.get('view');
    if (view === 'apps') {
      setWidgetParam('?view=apps&openexternal=1');
    }
  }, []);

  useInterval(() => {
    if (isWithinTradingHours(tradingHolidays, specialTradingDays)) {
      fetchData();
    }
  }, 60000);

  const fetchData = async () => {
    const data = [];
    try {
      const res = await makeApiCall(stocks_radar?.collection_url);
      if (!!res && res?.length > 0) {
        setDisplayName(res?.[0]?.Widgetname || 'Stocks In Focus');
        for (const item of res) {
          const entObj = !!item?.entities && JSON.parse(item?.entities);
          if (!!entObj?.entity_ids && entObj?.entity_ids?.length > 0) {
            const response = await makeApiCall(
              `/foapi/market/searchStocks?text=${entObj?.entity_ids?.[0]?.isin || entObj?.entity_ids?.[0]?.name}&instrumentId=2`,
            );
            if (response?.status) {
              const resData = processApiValues([response])[0];
              const stock = resData.find((s) => s.isin === entObj?.entity_ids?.[0]?.isin);
              if (!!stock) {
                data.push({
                  name: stock?.nse?.exchangeSymbol || stock?.bse?.exchangeSymbol || stock?.name,
                  summary: item?.Desc,
                  description: item?.Title,
                  heroImage: item?.FullImage || item?.Image,
                  id: item?.isin,
                  storySlug: item?.Link,
                  stockSlug: `${stock?.mojoId}/${encodeURIComponent(stock?.name?.toLowerCase() || stock?.entityname?.toLowerCase() || stock?.entity_name?.toLowerCase())}`,
                  value: stock?.nse?.last_price || stock?.bse?.last_price || '',
                  change: stock?.nse?.percent_change || stock?.bse?.percent_change || 0,
                });
              }
            }
          }
        }
        if (data?.length > 0) {
          setStockStories(data);
        }
      } else {
        setDisplayName('Stocks In Focus');
      }
    } catch (e) {
      console.error('Error in fetching data from stocks radar api, ', e);
    }
  };

  return (
    <div>
      <div
        styleName={`stocks-in-focus-live-ctx ${darkMode ? 'dark-mode' : ''} ${
          widget && !lightMode ? 'widget-mode' : ''
        }`}
        className={`${!widget ? 'container' : ''}`}
      >
        <div styleName="heading">
          <img src="/icons/stocksinfocus.svg" alt="Title Logo" />
          {!!displayName ? (
            <span styleName="title">{displayName}</span>
          ) : (
            <span styleName="title-shimmer"></span>
          )}
        </div>
        {stockStories?.length > 0 ? (
          <div styleName="stocks-ctx">
            <div styleName="stock-with-hero-image">
              <BQLink href={`${stockStories[0].storySlug}`} target="_blank">
                <div onClick={() => contentClick(userData, stockStories[0], pageType)}>
                  <img styleName="hero-image" src={`${stockStories[0].heroImage}`} alt="" />
                </div>
              </BQLink>
              <div styleName="stock-name-and-value">
                <BQLink
                  href={widgetLink(
                    `/stock/${stockStories[0].stockSlug}${widgetParam}`,
                    !!widgetParam,
                  )}
                  target="_blank"
                  styleName="stocks-detail"
                >
                  <div styleName="stock">
                    {stockStories[0]?.change > 0 ? (
                      <img
                        styleName="image"
                        src="/icons/arrow-top-right-thick.svg"
                        alt="Gainers Icon"
                      />
                    ) : (
                      <img
                        styleName="image"
                        src="/icons/arrow-bottom-right-thick.svg"
                        alt="Losers Icon"
                      />
                    )}
                    <div styleName="name">{stockStories[0].name}</div>
                    {(!widget || (widget && showValues)) && (
                      <div styleName="current-value">{stockStories[0]?.value}</div>
                    )}
                  </div>
                  {isStoryExclusive(stockStories[0]) && (
                    <span className="exclusive-icon">
                      <FourStarIcon color="#CE9445" size="16px" />
                    </span>
                  )}
                </BQLink>
              </div>
              <div onClick={() => contentClick(userData, stockStories[0], pageType)}>
                <BQLink href={`${stockStories[0].storySlug}`} target="_blank">
                  <div styleName="story-heading">{stockStories[0].description}</div>
                </BQLink>
              </div>
              <div styleName="story-summary">{stockStories[0].summary}</div>
            </div>
            <div styleName="stocks-with-text-only">
              {stockStories.slice(1, isSubscriber || widget ? 7 : 5).map((stock, index) => (
                <div
                  styleName={`stock-name-and-value-ctx ${
                    !isSubscriber && !widget ? 'default-user' : 'sub-user'
                  }`}
                  key={stock.id + index}
                >
                  <div styleName="stock-name-and-value">
                    <BQLink
                      href={widgetLink(`/stock/${stock.stockSlug}${widgetParam}`, !!widgetParam)}
                      target="_blank"
                      styleName="stocks-detail"
                    >
                      <div styleName="stock">
                        {stock?.change > 0 ? (
                          <img
                            styleName="image"
                            src="/icons/arrow-top-right-thick.svg"
                            alt="Gainers Icon"
                          />
                        ) : (
                          <img
                            styleName="image"
                            src="/icons/arrow-bottom-right-thick.svg"
                            alt="Losers Icon"
                          />
                        )}
                        <div styleName="name">{stock.name}</div>
                        {(!widget || (widget && showValues)) && (
                          <div styleName="current-value">{stock?.value}</div>
                        )}
                      </div>
                      {isStoryExclusive(stock) && (
                        <span className="exclusive-icon">
                          <FourStarIcon color="var(--dark-orange)" size="16px" />
                        </span>
                      )}
                    </BQLink>
                  </div>
                  <div onClick={() => contentClick(userData, stock, pageType)}>
                    <BQLink href={`${stock.storySlug}`} target="_blank">
                      <div styleName="story-heading">{stock.description}</div>
                    </BQLink>
                  </div>
                </div>
              ))}
              {!isSubscriber && !widget && isDesktop && (
                <div className="responsive-ad content-center desktop-only" styleName="ad-unit-320">
                  <AdWithPlaceholder
                    adtype="BudgetStocksInFocusLive_Desktop_Ad"
                    height="250px"
                    width="320px"
                    adUnit="Mid_300x250_1"
                    sizes={[[300, 250]]}
                  />
                </div>
              )}
            </div>
          </div>
        ) : (
          <div styleName="card-shimmer">
            <div styleName="stock-with-hero-image">
              <div styleName="hero-image-shimmer"></div>
              <div styleName="stock-name-shimmer"></div>
              <div styleName="story-title-shimmer">
                <div styleName="first-line"></div>
                <div styleName="second-line"></div>
              </div>
              <div styleName="story-subtitle-shimmer">
                <div styleName="first-line"></div>
                <div styleName="second-line"></div>
              </div>
            </div>
            <div styleName="stocks-with-text-only">
              {Array.apply(null, Array(6)).map((id) => (
                <div styleName="stock-name-and-value-ctx shimmer" key={id}>
                  <div styleName="stock-name-and-value">
                    <div styleName="stock-shimmer"></div>
                  </div>
                  <div>
                    <div styleName="story-heading-shimmer">
                      <div styleName="first-line"></div>
                      <div styleName="second-line"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  cdnHost: 'media.assettype.com',
  pageType: get(state, ['qt', 'pageType'], 'home-page'),
  stocks_radar: get(state, ['qt', 'config', 'features', 'stocks_radar'], {}),
});

export default connect(mapStateToProps)(BudgetStocksInFocusLive);
